import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { FiltersDropdown } from '@shared/components/ui/FiltersDropdown';
import { Search } from '@shared/components/ui/Search';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { ShopsFiltersPanel } from './ShopsFiltersPanel';

interface FiltersSectionProps {
  handleSearch: (value: string) => void;
  onFilterChange?: (conditions: FilterConditionOutput[]) => void;
}

export const FiltersSection = ({ handleSearch, onFilterChange }: FiltersSectionProps) => {
  const { t } = useTranslation('shopsModule');
  const { params, updateParam } = useQueryParams({
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
  });

  const [inputValue, setInputValue] = useState(params.search ?? '');

  const filtersFromUrl = useMemo(() => (Array.isArray(params.filters) ? params.filters : []), [params.filters]);

  useEffect(() => {
    const searchFromUrl = params.search ?? '';
    setInputValue((prev) => (prev !== searchFromUrl ? searchFromUrl : prev));
    handleSearch(searchFromUrl);
    onFilterChange?.(filtersFromUrl);
  }, [params.search, filtersFromUrl, handleSearch, onFilterChange]);

  const activeFiltersCount = useMemo(() => filtersFromUrl.length, [filtersFromUrl]);

  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      updateParam('filters', conditions);
      onFilterChange?.(conditions);
    },
    [updateParam, onFilterChange],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleSearchSubmit = useCallback(() => {
    const trimmedValue = inputValue.trim();
    updateParam('search', trimmedValue);
    handleSearch(trimmedValue);
  }, [inputValue, updateParam, handleSearch]);

  return (
    <>
      <Search
        placeholder={t('search.placeholder')}
        value={inputValue}
        onChange={handleInputChange}
        onEnter={handleSearchSubmit}
        dataTestId="search"
        inputDataTestId="search-input"
      />
      <FiltersDropdown
        title={t('filters.title', 'Filtros')}
        activeFiltersCount={activeFiltersCount}
        triggerTestId="filters-toggle-button"
      >
        <ShopsFiltersPanel initialConditions={filtersFromUrl} onChange={handleFilterChange} />
      </FiltersDropdown>
    </>
  );
};
